<template>
  <motlet-neo :options="motletOptions">
    <template v-slot:content="{ props }">
      <line-chart :settings="settings" :motlet="props.motlet" :store-name="storeName" :active-increment="activeIncrement" />
    </template>
  </motlet-neo>
</template>

<script>
import { merge } from 'lodash';
// BEGIN::Added.by.KWShin.2024.09.26.
import bus from '@/utils/bus';
// FINISH::Added.by.KWShin.2024.03.06.
// BEGIN::Added.by.SHJ.2026.04.27.
import { api } from '@/api/common/instances';
// FINISH::Added.by.SHJ.2026.04.27.
import lineChartable from './lineChartable';

export default {
  mixins: [lineChartable],
  data() {
    return {
      motletOptions: {
        id: 'dv/abap/os/mem',
        title: 'dv.title.mem',
        monitor: { monitorCode: 'C_ABAP_OS_MEM', eventCode: 'ABAP_OS_MEM', points: 60 },
        settings: { chart: { use: true } },
      },
      socketData: [],
    };
  },
  watch: {
    sstmId: {
      handler() {
        this.sendMessage();
      },
    },
    activeIncrement: {
      immediate: true,
      handler(value) {
        if (value) this.sendMessage();
      },
    },
    // BEGIN::Added.by.SHJ.2026.05.13. 언어 변경 시 헤더 타이틀 재합성
    '$i18n.locale'() {
      const field = this.motletOptions.settings.chart.field;
      if (field && field.code) this.chartFieldChanged(field.code);
    },
    // FINISH::Added.by.SHJ.2026.05.13.
  },
  methods: {
    wsBufferedDataHandler(key, responses) {
      const [response] = responses;
      const bound = response.length < this.monitor.points ? response.length : this.monitor.points;
      const data = response.slice(response.length - bound).map(item => {
        const {
          data: [data],
        } = item;
        return this.dataHandler({ ctime: item.ctime, ...data });
      });
      this.socketData.push({
        id: key.instId,
        data,
      });
      if (this.socketData.length == this.descendants.length) {
        this.data = merge([], this.socketData);
        this.socketData = [];
        this.prepend(['memUsedRate', 'swapUsedRate']);
        // BEGIN::Added.by.KWShin.2024.09.26.
        if (this.data && this.data.length > 0) {
          bus.$emit(`change:timestamp-${this.storeName}`, {
            eventCode: this.motletOptions.monitor.eventCode,
            timestamp: this.data[this.data.length - 1].ctime,
          });
        }
        // FINISH::Added.by.KWShin.2024.09.26.
        // BEGIN::Added.by.SHJ.2026.04.27.
        // WS 버퍼가 차트 X축 개수(monitor.points)만큼 채우지 못한 경우 앞쪽 빈 구간을 DB에서 보충한다.
        this.backfillFromDb();
        // FINISH::Added.by.SHJ.2026.04.27.
      }
    },
    wsDataHandler(key, response, ctime) {
      const [data] = response;
      const socketData = this.dataHandler({ ctime, ...data });
      const target = this.data.find(d => d.id == key.instId);
      if (target) {
        target.data.push(socketData);
        this.prepop();
      }
    },
    dataHandler(data) {
      const ctime = parseInt(data.ctime);
      return {
        ctime: ctime - (ctime % this.monitor.cycle), // db 데이터 간격 기준으로 실시간 데이터를 근사값으로 보정,
        stime: parseInt(data.stime),
        memUsedRate: Math.ceil((1 - parseInt(data.freeMem) / parseInt(data.physMem)) * 100),
        swapUsedRate: Math.ceil((1 - parseInt(data.swapFree) / parseInt(data.swapSize)) * 100),
      };
    },
    // BEGIN::Added.by.SHJ.2026.04.27.
    /**
     * WS 버퍼가 monitor.points 만큼 채워지지 않았을 때 DB에서 부족한 앞 구간을 조회해
     * 이미 비어있는 슬롯(memUsedRate===undefined)에만 값을 주입한다. 실제 WS 데이터는 덮어쓰지 않는다.
     */
    async backfillFromDb() {
      if (!this.data || this.data.length === 0) return;
      const firstInst = this.data[0].data;
      if (!firstInst || firstInst.length === 0) return;
      const realPoints = firstInst.filter(d => d.memUsedRate !== undefined && d.memUsedRate !== '');
      if (realPoints.length === firstInst.length) return;
      const beginYmdhms = firstInst[0].ctime;
      const endYmdhms = realPoints.length > 0 ? realPoints[0].ctime - this.monitor.cycle : firstInst[firstInst.length - 1].ctime;
      if (endYmdhms < beginYmdhms) return;
      const params = {
        beginYmdhms,
        endYmdhms,
        sstmId: this.sstmId,
        motletCode: this.monitor.monitorCode,
        ...this.dataParam,
      };
      try {
        const { data: results } = await api.get('/motlet-data/chart', { params });
        if (!results || results.length === 0) return;
        results.forEach(result => {
          const inst = this.data.find(d => d.id === result.key.instId);
          if (!inst) return;
          result.data.forEach(db => {
            const dbCtime = parseInt(db.ctime);
            const slot = inst.data.find(p => p.ctime === dbCtime);
            if (slot && (slot.memUsedRate === undefined || slot.memUsedRate === '')) {
              slot.memUsedRate = parseInt(db.memUsedRate);
              slot.swapUsedRate = parseInt(db.swapUsedRate);
              if (db.stime) slot.stime = parseInt(db.stime);
            }
          });
        });
      } catch (e) {
        // DB 조회 실패 시 차트는 WS 실시간 수신으로 점진적으로 채워지므로 무시한다.
        console.warn('[Memory] backfillFromDb failed', e);
      }
    },
    // FINISH::Added.by.SHJ.2026.04.27.
    sendMessage() {
      this.$websocket.realtime.send({
        requests: this.descendants.map(descendant => ({
          key: { name: 'rawOsMem', sstmId: descendant.systemId, instId: descendant.id },
          fields: ['stime', 'freeMem', 'physMem', 'swapFree', 'swapSize'],
          handler: {
            buffer: this.wsBufferedDataHandler,
            every: this.wsDataHandler,
          },
        })),
      });
    },
    /**
     * Added.by.KWShin.20204.09.26.
     * 부모가 차트 필드 항목 설정값을 가져온 뒤 호출해준다.
     * Motlet 메뉴에서 차트 필드 설정이 변경되었을 때에도
     * 편의상 (로직이 동일하므로) 자기 스스로 호출한다.
     */
    chartFieldChanged(code) {
      this.motletOptions.settings.chart.field = this.chartFields.find(f => f.code == code);
      // setChartField()는 lineChartable.js에 mixins된 motletable.js에 선언된 메소드로 store를 변경시켜준다.
      // 설정값은 API로 DB를 갔다오므로 지연이 발생하고
      // store는 MotletNeo.vue가 생성되는 시점에 등록되므로
      // 부모가 설정 변경을 알려줄 때 다시 한번 store 값을 변경해준다.
      this.setChartField(code);
      // 차트 항목이 변경되었으므로 서브타이틀을 바꾸어 준다.2026.06.16.
      this.motletOptions.subTitle = `(${this.motletOptions.settings.chart.field.text})`;
    },
  },
  created() {
    // BEGIN::Added.by.KWShin.2024.09.26.
    bus.$on(`update:chartFieldCode-${this.motletOptions.id}`, chartFieldCode => {
      this.chartFieldChanged(chartFieldCode);
    });
    // FINISH::Added.by.KWShin.2024.09.26.
  },
  destroyed() {
    // BEGIN::Added.by.KWShin.2024.09.26.
    bus.$off(`update:chartFieldCode-${this.motletOptions.id}`);
    // FINISH::Added.by.KWShin.2024.09.26.
  },
};
</script>
